<script lang="ts" context="module">
  /**
   * this flag enables / disables the no-plan as default if no service is selected
   */
  const NO_PLAN_DEFAULT = false;

  type UseAsType = 'Safeguard' | 'Setup' | 'CareCoating';
  type UseAsToAnyMap = { [key in UseAsType]: any };
</script>

<script lang="ts">
  import { onDestroy, onMount } from 'svelte';

  import { getAppConfig } from '../../../common/config/app-config';

  import { getIsPrimeNoPlan, getServiceJoodCardType } from '../../../common/joodUtils';
  import {
    getJoodExpiredMembershipType,
    getJoodMembershipType,
    getStr,
    refPropertySorter,
    slideOpenAnimation,
  } from '../../../common/util';
  import JoodExpire from '../../common/components/productTile/components/JoodExpire.svelte';
  import JoodSavingsAccordionCard from '../../product-listing-search/components/JoodSavingsAccordionCard.svelte';
  import {
    pdpCareCoatingServiceSelectedItem,
    pdpSafeguardServiceSelectedItem,
    pdpServicesSaveState,
    pdpSetupServiceSelectedItem,
    setMixpanelDataForService,
  } from '../store/pdp_store';
  import PdpAdditionalServicesMiniCard from './PDPAdditionalServicesMiniCard.svelte';
  import PdpSafeguardTile from './PDPSafeguardTile.svelte';
  import PdpSafeguardTileWallet from './PDPSafeguardTileWallet.svelte';
  import PdpServiceMiniCard from './PDPServiceMiniCard.svelte';
  import PdpVasPriceTile from './PDPVasPriceTile.svelte';

  export let data: any;
  export let labelData: any;
  export let lmdConfigurations: any;
  export let useAs: UseAsType = 'Safeguard';

  let activeBaseVasPrice = null;
  let activeAddonVasPrice = null;
  let isNoPlanOptionSelected = false;
  let initialized = false;
  const expiredUserType = getJoodExpiredMembershipType();
  const userJoodLevel = expiredUserType ? expiredUserType : getJoodMembershipType();
  const isPrimeNoPlan = getIsPrimeNoPlan(userJoodLevel, data?.vasPrices);

  $: baseServiceVasPrices = getSortedVasPrices(
    data?.vasPrices?.filter(vp => !vp.additionalAwsProductCode),
    false
  );
  $: noPlanBaseIndex = baseServiceVasPrices?.findIndex(x => x?.noPlanOption);
  $: allAddonVasPrices = getSortedVasPrices(
    data?.vasPrices?.filter(vp => !!vp.additionalAwsProductCode),
    true
  );
  $: availableAddonVasPrices = getAddonVasPricesForActiveBaseVasPrice(allAddonVasPrices, activeBaseVasPrice);
  $: joodSavingsAccordionVisible = getJoodSavingsAccordionVisibility(baseServiceVasPrices);
  $: if (initialized) {
    updatePDPStoreWithSelectedItem(activeBaseVasPrice, activeAddonVasPrice);
  }
  $: isUpgradeService = data?.upgradeVasEntry;
  $: hideNameField = isUpgradeService || useAs === 'CareCoating';

  const joodServiceCard = checkJoodServicePrice();
  const { joodExpiredFlag, benefitType = 'DISCOUNT' } = getAppConfig();
  const ExpireduserJoodLevel = getJoodExpiredMembershipType();

  onMount(() => {
    // if saved state contains a selected item, preselect it
    const saveStateMap: UseAsToAnyMap = {
      Safeguard: $pdpServicesSaveState.safeguard,
      Setup: $pdpServicesSaveState.setup,
      CareCoating: $pdpServicesSaveState.careCoating,
    };
    const saveState = saveStateMap[useAs];

    if (saveState) {
      ({ activeBaseVasPrice = null, activeAddonVasPrice = null, isNoPlanOptionSelected = false } = saveState);
    }
    initialized = true;
  });
  onDestroy(() => {
    const saveStateFnMap: UseAsToAnyMap = {
      Safeguard: pdpServicesSaveState.setSafeguard,
      Setup: pdpServicesSaveState.setSetup,
      CareCoating: pdpServicesSaveState.setCareCoating,
    };
    const saveStateFn = saveStateFnMap[useAs];

    saveStateFn(activeBaseVasPrice, activeAddonVasPrice, isNoPlanOptionSelected);
  });

  function checkJoodServicePrice() {
    let userType = userJoodLevel;
    switch (userJoodLevel) {
      case 'BASICPRIME':
        userType = getPrimeServicePrice('basicPrimeDiscountPrice');
        break;
      case 'SMARTPRIME':
        userType = getPrimeServicePrice('smartPrimeDiscountPrice');
        break;
      case 'VIPPRIME':
        userType = getPrimeServicePrice('vipPrimeDiscountPrice');
        break;
    }
    return userType;
  }

  function getPrimeServicePrice(primeType) {
    const primePrices = data?.vasPrices;
    for (let i = 0; i < primePrices?.length; i++) {
      if (primePrices[i]?.[primeType]?.value > 0) {
        return userJoodLevel;
      }
    }
    return 'NOPRIME';
  }

  function updatePDPStoreWithSelectedItem(activeBaseVasPrice, activeAddonVasPrice) {
    const activeItem = activeAddonVasPrice ?? activeBaseVasPrice;
    const {
      noPlanProductCode,
      serviceProductCode,
      combinedVas,
      vasGroup,
      showApplyService,
      upgradeVasEntry,
      installService,
    } = data;
    const {
      CSRFToken,
      productDetails: { code: pdpProductCode },
    } = getAppConfig();

    const targetStoreMap: UseAsToAnyMap = {
      Safeguard: pdpSafeguardServiceSelectedItem,
      Setup: pdpSetupServiceSelectedItem,
      CareCoating: pdpCareCoatingServiceSelectedItem,
    };
    const targetStore = targetStoreMap[useAs];

    if (activeItem) {
      const {
        noPlanOption,
        vasType: { code },
        priceType,
        priceTo,
        productCodes,
        additionalAwsProductCode,
        additionalAwsVasGroup,
        defaultWarranty,
      } = activeItem;
      const vasProductCode = noPlanOption ? noPlanProductCode : (productCodes ?? serviceProductCode);

      let addonPriceForUser = 0;
      switch (userJoodLevel) {
        case 'NOPRIME':
          addonPriceForUser = activeItem?.discountedPrice?.value || activeItem?.servicePrice?.value;
          break;
        case 'BASICPRIME':
          addonPriceForUser =
            activeItem?.basicPrimeDiscountPrice?.value ||
            activeItem?.discountedPrice?.value ||
            activeItem?.servicePrice?.value;
          break;
        case 'VIPPRIME':
          addonPriceForUser =
            activeItem?.vipPrimeDiscountPrice?.value ||
            activeItem?.discountedPrice?.value ||
            activeItem?.servicePrice?.value;
          break;
      }

      setMixpanelDataForService(
        useAs,
        activeBaseVasPrice?.vasType?.labelEn || 'no plan',
        activeAddonVasPrice?.vasType?.labelEn || 'no addon',
        addonPriceForUser,
        vasProductCode
      );

      targetStore.set({
        vasProductCode,
        vasType: code,
        priceType,
        priceTo,
        productCode: pdpProductCode,
        additionalAwsProductCode,
        additionalAwsVasGroup,
        combinedVas,
        qty: 1,
        vasGroup,
        showApplyService,
        CSRFToken,
        defaultWarranty,
        upgradeService: upgradeVasEntry,
        installService,

        //noPlanOption
        ...(noPlanOption && { noPlanOption }),
      });
    } else {
      setMixpanelDataForService(useAs); // no param supplied to nullify it
      targetStore.set(null);
    }
  }
  function getSortedVasPrices(vasPrices, isAddonVasPriceList) {
    const sortedVasPrices = [...vasPrices].sort(refPropertySorter(['PRICE_TIER', 'NO_PLAN_PRICE'], 'priceType'));
    if (!isAddonVasPriceList) {
      // remove condition && NO_PLAN_DEFAULT
      const isUpgradeService = data?.upgradeVasEntry;
      if (isUpgradeService) {
        const freeVasPriceIndex = sortedVasPrices?.findIndex(
          vp => vp?.servicePrice?.value === 0 && activeBaseVasPrice === null
        );
        if (freeVasPriceIndex > -1) {
          activeBaseVasPrice = sortedVasPrices[freeVasPriceIndex];
        }
      } else {
        // activeBaseVasPrice = sortedVasPrices?.find(vp => vp.noPlanOption) || null;
      }
    }
    return sortedVasPrices;
  }
  function getAddonVasPricesForActiveBaseVasPrice(allAddonVasPrices, activeBaseVasPrice) {
    return allAddonVasPrices?.filter(x => x?.vasType?.code === activeBaseVasPrice?.vasType?.code) || [];
  }
  function handleVasPriceTileClick(vasPrice, isAddonVasPrice) {
    if (isAddonVasPrice) {
      activeAddonVasPrice = activeAddonVasPrice === vasPrice ? null : vasPrice;
    } else {
      if (activeBaseVasPrice === vasPrice) {
        if (NO_PLAN_DEFAULT) activeBaseVasPrice = noPlanBaseIndex === -1 ? null : baseServiceVasPrices[noPlanBaseIndex];
        else if (!isUpgradeService) {
          activeBaseVasPrice = null;
        }
      } else {
        activeBaseVasPrice = vasPrice;
      }

      isNoPlanOptionSelected = !!activeBaseVasPrice && !!vasPrice?.noPlanOption;
      activeAddonVasPrice = null;
    }
  }
  function getJoodSavingsAccordionVisibility(vasPrices) {
    const isNonGoldUser = userJoodLevel !== 'VIPPRIME' || ExpireduserJoodLevel === 'BASICPRIME';
    const isSmartPrimeUser = userJoodLevel === 'SMARTPRIME';
    const joodPriceType =
      userJoodLevel === 'BASICPRIME' || ExpireduserJoodLevel === 'BASICPRIME'
        ? 'basicPrimeDiscountPrice'
        : 'vipPrimeDiscountPrice';
    const hasGoldDiscounts = vasPrices?.every(x => !!x[joodPriceType] || x.noPlanOption);
    return isNonGoldUser && hasGoldDiscounts && !isSmartPrimeUser;
  }
</script>

<PdpServiceMiniCard data={labelData} serviceBlockType={data.serviceBlockType}>
  <section class="pdp-safeguard-service user-{joodServiceCard} use-as-{useAs}">
    <section class="base-service-list">
      {#each baseServiceVasPrices as vasPrice}
        {#if useAs === 'Setup'}
          <PdpVasPriceTile
            variant="base"
            data={vasPrice}
            active={vasPrice === activeBaseVasPrice}
            on:click={() => handleVasPriceTileClick(vasPrice, false)}
          />
        {:else if benefitType === 'WALLET'}
          <PdpSafeguardTileWallet
            {joodServiceCard}
            addonView={false}
            data={vasPrice}
            active={vasPrice === activeBaseVasPrice}
            {hideNameField}
            serviceJoodCardType={getServiceJoodCardType(userJoodLevel, ExpireduserJoodLevel, vasPrice, isPrimeNoPlan)}
            on:click={() => handleVasPriceTileClick(vasPrice, false)}
          />
        {:else}
          <PdpSafeguardTile
            {joodServiceCard}
            addonView={false}
            data={vasPrice}
            active={vasPrice === activeBaseVasPrice}
            {hideNameField}
            serviceJoodCardType={getServiceJoodCardType(userJoodLevel, ExpireduserJoodLevel, vasPrice, isPrimeNoPlan)}
            on:click={() => handleVasPriceTileClick(vasPrice, false)}
          />
        {/if}
      {/each}
    </section>

    {#if isNoPlanOptionSelected}
      <section class="addon no-plan-warning" transition:slideOpenAnimation>
        <p class="addon-first-line">
          <span class="orange-text">{getStr('extra.pdp.service.areyousure')}</span>
          {getStr('extra.pdp.service.yourdevice')}
        </p>
      </section>
    {/if}

    {#if availableAddonVasPrices?.length > 0}
      <section class="addon" transition:slideOpenAnimation>
        <p class="addon-first-line">
          <span class="green-text">{getStr('extra.pdp.service.smartchoice')}</span>
          {getStr('extra.pdp.service.dontmissout')}
        </p>
        <p class="addon-second-line">
          {getStr('extra.pdp.service.gobeyondmanufacturer')}
        </p>
        <section class="addon-list">
          {#each availableAddonVasPrices as vasPrice}
            {#if useAs === 'Setup'}
              <PdpVasPriceTile
                variant="addon"
                data={vasPrice}
                active={vasPrice === activeAddonVasPrice}
                on:click={() => handleVasPriceTileClick(vasPrice, true)}
              />
            {:else}
              <PdpSafeguardTile
                addonView={true}
                data={vasPrice}
                active={vasPrice === activeAddonVasPrice}
                {hideNameField}
                serviceJoodCardType={getServiceJoodCardType(
                  userJoodLevel,
                  ExpireduserJoodLevel,
                  vasPrice,
                  isPrimeNoPlan
                )}
                on:click={() => handleVasPriceTileClick(vasPrice, true)}
              />
            {/if}
          {/each}
        </section>
      </section>
    {/if}

    <PdpAdditionalServicesMiniCard data={lmdConfigurations} />
  </section>
  {#if benefitType === 'DISCOUNT' && joodSavingsAccordionVisible}
    <JoodSavingsAccordionCard variant="pdp-service" data={baseServiceVasPrices} />
  {/if}
  {#if joodExpiredFlag && joodServiceCard !== 'NOPRIME' && benefitType === 'WALLET'}
    <section class="my-3">
      <JoodExpire accordion={true} />
    </section>
  {/if}
</PdpServiceMiniCard>

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  .pdp-safeguard-service {
    .base-service-list {
      display: flex;
      gap: 12px;
      overflow: auto;
    }
    .addon {
      margin-top: 12px;

      p {
        margin: 0;
        font-size: 14px;
        line-height: 16px;

        &.addon-first-line {
          font-weight: 500;
          color: $neutral-dark-03;
        }
        &.addon-second-line {
          font-weight: 400;
          color: $neutral-dark-02;
          margin-top: 4px;
        }
        .green-text {
          color: $color-green;
        }
        .orange-text {
          color: $functional-orange;
        }
      }
      .addon-list {
        display: flex;
        gap: 12px;
        padding-top: 14px;
      }
    }

    // &:not(.use-as-Setup) {
    //   &.user-VIPPRIME,
    //   &.user-BASICPRIME {
    //     padding: 18px 12px;
    //     border-radius: 12px;
    //   }

    // &.user-BASICPRIME {
    //   background-color: $jood-blue-light;
    // }
    // &.user-VIPPRIME {
    //   background-color: $jood-gold-light;
    // }
    // }
  }
</style>
